/**
 * IonUsageMeter (WS 2.2 Task A; PRO token variant added 2026-07-16;
 * monthly window + self-service pacing added 2026-07-16, Ion AI limits
 * system; DEMO switched from a request-count meter to the same token meters
 * as PRO/batemo + the request-count cap removed entirely, 2026-07-17)
 * ==================================================================
 * Persistent daily-budget meter, seamless: no standalone tile/card, matching
 * the just-de-tiled deep-dive meter it sits next to on `/assistant` and in
 * the slide-over.
 *
 * ALL tiers with a chat budget (DEMO / PRO / batemo) get the same
 * "X of N Insights tokens today/this week/this month" meters — a
 * COST-derived display unit ($0.25 USD = 1 token, see backend
 * `chat/token_units.py`), NOT a raw USD amount. Reads `insightsTokenBudget` /
 * `weeklyTokenBudget` / `monthlyTokenBudget` (populated by
 * `ionAccessService.bootstrapChatAccess` from `GET /chat/usage/me`).
 *
 * Below the meters, `PacingInfo` renders either:
 *   - PRO / batemo: "PRO: adjustable" + the self-service daily/weekly
 *     pacing control (`PacingEditor`) — monthly itself is never editable
 *     here (admin-only for every profile, DEC-B1).
 *   - DEMO: "DEMO: fixed" — informational only, no editor (the backend
 *     rejects a DEMO self-service pacing write with 403 `pacing_not_editable`,
 *     see `chat/router.py::set_my_pacing`).
 *
 * Self-guards: each meter renders null when its own limit is unknown/
 * unlimited (`limit == null`); the whole component renders null for FREE /
 * no-subscription (no `tierProfile`).
 *
 * VISIBILITY (2026-07-20): the meters stay hidden until a window is at least
 * 80% consumed — the limits should be unobtrusive and only surface as you
 * approach them. "Near" = the server-computed `warning` flag (its own 80%
 * per-window threshold) OR a local `pct >= 80` fallback. On staging the live
 * turn-end `quota` stream updates these signals mid-chat, so the meter pops in
 * the moment a turn crosses 80%; without the live stream it appears on the next
 * `bootstrapChatAccess`. DEMO (no pacing control) hides the whole group until
 * near; PRO/batemo keep the pacing editor reachable regardless.
 */
import { useState } from 'preact/hooks'
import {
  insightsTokenBudget,
  weeklyTokenBudget,
  monthlyTokenBudget,
  tierProfile,
} from '../../state/chatStore.js'
import { setPacingLimits } from '../../services/ionAccessService.js'
import { pacingEditorOpen, closePacingEditor } from '../../state/pacingEditorState.js'

// Mirrors backend `settings.ion_token_usd_value` (chat/token_units.py) — the
// deploy-time $0.25/token constant. Used ONLY to convert a user's token-unit
// pacing input back into the USD figure the self-service endpoint's wire
// contract expects; the customer never SEES a USD number anywhere in this
// component (DEC-6/§1.3 — tokens only for non-admin surfaces).
const INSIGHTS_TOKEN_USD_VALUE = 0.25

function isTieredProfile() {
  const p = tierProfile.value
  return p === 'pro' || p === 'demo' || p === 'batemo'
}

function fmtAmount(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

// A window is "near" (worth surfacing) once it is >= 80% consumed. Prefer the
// server-computed `warning` flag (its own independent per-window 80% threshold,
// §2.2); fall back to a local pct >= 80 so the gate still works if `warning`
// wasn't populated. Returns false for unknown/unlimited/invalid limits.
function isWindowNear(budget) {
  if (!budget) return false
  const { used, limit, warning } = budget
  if (limit == null) return false
  const limitNum = Number(limit)
  if (!Number.isFinite(limitNum) || limitNum <= 0) return false
  const usedNum = Number(used) || 0
  const pct = Math.min(100, Math.round((Math.min(usedNum, limitNum) / limitNum) * 100))
  return Boolean(warning) || pct >= 80
}

export function IonUsageMeter() {
  if (!isTieredProfile()) return null

  const anyNear =
    isWindowNear(insightsTokenBudget.value) ||
    isWindowNear(weeklyTokenBudget.value) ||
    isWindowNear(monthlyTokenBudget.value)

  // The tile exists to show meters. It used to render for PRO/batemo even with
  // no window near its cap, purely so the pacing LINK had somewhere to live —
  // producing a full-width strip under the header holding one sentence and one
  // link, and nothing else. The pacing control now lives in the Ion header
  // (ChatPanelHeader), so the tile can hide whenever there is nothing to
  // report, for every tier.
  //
  // It still renders when the editor is open, because that is where the editor
  // appears — opening it from the header has to make something visible.
  const editorOpen = pacingEditorOpen.value
  if (!anyNear && !editorOpen) return null

  return (
    <div class="ion-usage-meter-group">
      <TokenMeter />
      <WeeklyTokenMeter />
      <MonthlyTokenMeter />
      <PacingInfo />
    </div>
  )
}

function TokenMeter() {
  const { used, limit, warning } = insightsTokenBudget.value
  if (limit == null) return null

  const limitNum = Number(limit)
  if (!Number.isFinite(limitNum) || limitNum <= 0) return null
  const usedNum = Number(used) || 0

  const clampedUsed = Math.min(usedNum, limitNum)
  const pct = Math.min(100, Math.round((clampedUsed / limitNum) * 100))
  // Only surface the meter once this window is >= 80% consumed (server 80%
  // `warning` flag, or local pct fallback). Below that it stays hidden.
  const near = warning || pct >= 80
  if (!near) return null

  return (
    <div
      class={`ion-usage-meter${near ? ' ion-usage-meter--warn' : ''}`}
      role="status"
      aria-live="polite"
      aria-label={`${fmtAmount(clampedUsed)} of ${fmtAmount(limitNum)} Insights tokens used today.`}
    >
      <div class="ion-usage-meter__row">
        <span class="ion-usage-meter__label">
          <span class="ion-usage-meter__count">
            {fmtAmount(clampedUsed)} of {fmtAmount(limitNum)}
          </span>{' '}
          Insights tokens today
        </span>
      </div>
      <div class="ion-usage-meter__track">
        <div class="ion-usage-meter__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

/**
 * Weekly sibling of TokenMeter (parity fix, 2026-07-17) — the window
 * between daily and monthly. Mirrors TokenMeter's rendering exactly, just
 * reading the weekly signal and using the server-computed `warning` flag
 * (independent 80%-threshold evaluation per window, §2.2) in addition to
 * the remaining-budget heuristic.
 */
function WeeklyTokenMeter() {
  const { used, limit, warning } = weeklyTokenBudget.value
  if (limit == null) return null

  const limitNum = Number(limit)
  if (!Number.isFinite(limitNum) || limitNum <= 0) return null
  const usedNum = Number(used) || 0

  const clampedUsed = Math.min(usedNum, limitNum)
  const pct = Math.min(100, Math.round((clampedUsed / limitNum) * 100))
  const near = warning || pct >= 80
  if (!near) return null

  return (
    <div
      class={`ion-usage-meter${near ? ' ion-usage-meter--warn' : ''}`}
      role="status"
      aria-live="polite"
      aria-label={`${fmtAmount(clampedUsed)} of ${fmtAmount(limitNum)} Insights tokens used this week.`}
    >
      <div class="ion-usage-meter__row">
        <span class="ion-usage-meter__label">
          <span class="ion-usage-meter__count">
            {fmtAmount(clampedUsed)} of {fmtAmount(limitNum)}
          </span>{' '}
          Insights tokens this week
        </span>
      </div>
      <div class="ion-usage-meter__track">
        <div class="ion-usage-meter__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

/**
 * Monthly sibling of TokenMeter (Ion AI limits system, 2026-07). Renders
 * "for your whole Demo" instead of a bar/date when `resetAt === 'never'`
 * (DEMO's one-time total-for-the-account ceiling) — this is the common case
 * for DEMO, since DEMO's monthly window never resets (§2.3).
 */
function MonthlyTokenMeter() {
  const { used, limit, resetAt, warning } = monthlyTokenBudget.value
  if (limit == null) return null

  const limitNum = Number(limit)
  if (!Number.isFinite(limitNum) || limitNum <= 0) return null
  const usedNum = Number(used) || 0

  const clampedUsed = Math.min(usedNum, limitNum)
  const pct = Math.min(100, Math.round((clampedUsed / limitNum) * 100))
  const near = warning || pct >= 80
  if (!near) return null

  return (
    <div
      class={`ion-usage-meter${near ? ' ion-usage-meter--warn' : ''}`}
      role="status"
      aria-live="polite"
      aria-label={`${fmtAmount(clampedUsed)} of ${fmtAmount(limitNum)} Insights tokens used this month.`}
    >
      <div class="ion-usage-meter__row">
        <span class="ion-usage-meter__label">
          <span class="ion-usage-meter__count">
            {fmtAmount(clampedUsed)} of {fmtAmount(limitNum)}
          </span>{' '}
          Insights tokens this month
          {resetAt === 'never' ? ' (for your whole Demo)' : ''}
        </span>
      </div>
      <div class="ion-usage-meter__track">
        <div class="ion-usage-meter__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

/**
 * PRO-editable vs DEMO-fixed info line (Ion AI limits system, 2026-07-17).
 * PRO/batemo get the info line PLUS the self-service pacing editor below it;
 * DEMO gets an informational note only — the backend structurally rejects a
 * DEMO self-service pacing write (`PUT /chat/usage/me/pacing` → 403
 * `pacing_not_editable`, see `chat/router.py::set_my_pacing`), so there is no
 * editor to show. Renders null for any other profile.
 */
function PacingInfo() {
  const profile = tierProfile.value
  if (profile === 'pro' || profile === 'batemo') {
    // The "PRO: adjustable" caption used to be permanent, explaining a link
    // below it. The link is now a header button, so the caption only appears
    // WITH the editor it introduces — otherwise it was a standing sentence
    // about a control that had moved away.
    if (!pacingEditorOpen.value) return null
    return (
      <>
        <p class="ion-usage-meter__pacing-note">
          PRO: adjustable — set your own daily and weekly caps.
        </p>
        <PacingEditor />
      </>
    )
  }
  if (profile === 'demo') {
    return (
      <p class="ion-usage-meter__pacing-note ion-usage-meter__pacing-note--fixed">
        DEMO: fixed — daily/weekly caps aren't adjustable on this plan.
      </p>
    )
  }
  return null
}

/**
 * Self-service daily/weekly pacing (PRO/batemo only — Ion AI limits system
 * §5 Phase 3). Monthly is never editable here — it's the plan allotment,
 * admin-only for every profile (DEC-B1); this control only ever writes
 * daily/weekly.
 *
 * Opened from the Ion header (ChatPanelHeader's pacing button) via
 * `pacingEditorOpen`, not from a link inside this tile. It used to be a
 * link-style toggle here, which forced the whole meter tile to render for
 * PRO/batemo even when no window was near its cap — a full-width strip holding
 * one sentence and one link. See pacingEditorState.js.
 */
function PacingEditor() {
  const [daily, setDaily] = useState('')
  const [weekly, setWeekly] = useState('')
  const [dailyUnlimited, setDailyUnlimited] = useState(false)
  const [weeklyUnlimited, setWeeklyUnlimited] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  if (tierProfile.value !== 'pro' && tierProfile.value !== 'batemo') return null

  // Converts a token-unit input to the USD Decimal-string the backend wire
  // contract expects. `null` (no cap) whenever the field is checked
  // "unlimited" OR left blank — a blank field with no explicit value is
  // treated as "no cap" rather than silently defaulting to some number.
  function tokensToUsdOrNull(rawInput, unlimited) {
    if (unlimited || rawInput === '') return null
    const n = Number(rawInput)
    if (!Number.isFinite(n) || n < 0) return null
    return String(n * INSIGHTS_TOKEN_USD_VALUE)
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    const result = await setPacingLimits({
      dailyLimitUsd: tokensToUsdOrNull(daily, dailyUnlimited),
      weeklyLimitUsd: tokensToUsdOrNull(weekly, weeklyUnlimited),
    })
    setSaving(false)
    if (result.ok) {
      closePacingEditor()
    } else {
      setError(result.error?.code === 'pacing_exceeds_monthly'
        ? `That exceeds your monthly Insights Token budget.`
        : 'Could not save — please try again.')
    }
  }

  return (
    <div class="ion-usage-meter__pacing-editor">
      {/* Each row is `display: contents` in CSS, so the label/checkbox pairs
          become direct grid items of the editor and line up column-for-column
          across both rows. Keeping the wrappers means the markup still reads
          as "one row per cap" without a wrapper box breaking the alignment. */}
      <div class="ion-usage-meter__pacing-row">
        <label class="ion-usage-meter__pacing-field">
          <span class="ion-usage-meter__pacing-label">Daily cap (Insights Tokens)</span>
          <input
            class="ion-usage-meter__pacing-input"
            type="number"
            min="0"
            step="0.1"
            value={daily}
            disabled={dailyUnlimited}
            onInput={(e) => setDaily(e.currentTarget.value)}
          />
        </label>
        <label class="ion-usage-meter__pacing-checkbox">
          <input
            type="checkbox"
            checked={dailyUnlimited}
            onChange={(e) => setDailyUnlimited(e.currentTarget.checked)}
          />
          No daily cap
        </label>
      </div>
      <div class="ion-usage-meter__pacing-row">
        <label class="ion-usage-meter__pacing-field">
          <span class="ion-usage-meter__pacing-label">Weekly cap (Insights Tokens)</span>
          <input
            class="ion-usage-meter__pacing-input"
            type="number"
            min="0"
            step="0.1"
            value={weekly}
            disabled={weeklyUnlimited}
            onInput={(e) => setWeekly(e.currentTarget.value)}
          />
        </label>
        <label class="ion-usage-meter__pacing-checkbox">
          <input
            type="checkbox"
            checked={weeklyUnlimited}
            onChange={(e) => setWeeklyUnlimited(e.currentTarget.checked)}
          />
          No weekly cap
        </label>
      </div>
      {error && (
        <div class="ion-usage-meter__pacing-error" role="alert">
          {error}
        </div>
      )}
      {/* ion-btn / ion-btn--ghost are the house button classes (ion.css) —
          these two carried NO class at all and so rendered as raw browser
          buttons inside an otherwise styled panel. */}
      <div class="ion-usage-meter__pacing-actions">
        <button
          type="button"
          class="ion-btn ion-btn--small"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          class="ion-btn ion-btn--small ion-btn--ghost"
          onClick={closePacingEditor}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
